// OperationExpandedRow.tsx
import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Space, Tag, Button, Image, Typography, Skeleton, message } from 'antd';
import {
  IdcardOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  PictureOutlined,
  ExportOutlined,
  CopyOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../../../constants/config';
import type { OperationType } from './OperationTypeIcon';
import { useTheme } from '../../../contexts/ThemeContext';

const { Text } = Typography;

export interface Operation {
  id: string;
  operationNumber?: string;
  type: OperationType;
  userId: string;
  adminId: string;
  amount: number | null;
  bonus: number;
  receipt?: string;
  status: string;
  message?: string;
  createdAt: Date;
  executionTime: number;
  requestData?: any;
  linkedTransferId?: number | null;
  user: {
    id: string;
    username: string;
    accountName?: string;
  };
}

interface OperationExpandedRowProps {
  record: Operation;
}

export const OperationExpandedRow: React.FC<OperationExpandedRowProps> = ({ record }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [imageError, setImageError] = useState<boolean>(false);
  const { theme } = useTheme();

  useEffect(() => {
    if (record.receipt) {
      loadReceipt(record.receipt);
    }

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [record.receipt]);

  const loadReceipt = async (receiptPath: string) => {
    setImageLoading(true);
    setImageError(false);

    try {
      // Limpiar la ruta del recibo
      let cleanPath = receiptPath;
      if (cleanPath.startsWith('/uploads/')) {
        cleanPath = cleanPath.substring(9);
      } else if (cleanPath.startsWith('uploads/')) {
        cleanPath = cleanPath.substring(8);
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setImageError(true);
        return;
      }

      // Si es una URL externa, no cargar con autenticación
      if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
        setImageUrl(cleanPath);
        setImageLoading(false);
        return;
      }

      const apiUrl = `${API_BASE_URL}/api/panel/uploads/${cleanPath}`;
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });

      const blobUrl = URL.createObjectURL(response.data);
      setImageUrl(blobUrl);
    } catch (error) {
      console.error('Error loading receipt:', error);
      setImageError(true);
    } finally {
      setImageLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copiado al portapapeles`);
  };

  const openImageInNewTab = () => {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  };

  const bgColor = theme === 'dark' ? '#0a0a0a' : '#fafafa';
  const jsonBgColor = theme === 'dark' ? '#1a1a1a' : '#f5f5f5';
  const jsonTextColor = theme === 'dark' ? '#d4d4d4' : '#333333';

  return (
    <div style={{ padding: '16px 24px', background: bgColor }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Información Detallada */}
        <Card
          size="small"
          title={
            <Space>
              <IdcardOutlined />
              <span>Información Detallada</span>
            </Space>
          }
        >
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
            <Descriptions.Item label={<><IdcardOutlined /> ID de Operación</>}>
              <Space size="small">
                <Text code>{record.id}</Text>
                <Button
                  type="link"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(record.id, 'ID')}
                />
              </Space>
            </Descriptions.Item>

            {record.operationNumber && (
              <Descriptions.Item label="Número de Operación">
                <Tag color="blue">{record.operationNumber}</Tag>
              </Descriptions.Item>
            )}

            <Descriptions.Item label={<><CalendarOutlined /> Fecha de Creación</>}>
              <Text>
                {new Date(record.createdAt).toLocaleString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </Text>
            </Descriptions.Item>

            <Descriptions.Item label={<><ClockCircleOutlined /> Tiempo de Ejecución</>}>
              <Tag color={record.executionTime < 1000 ? 'green' : record.executionTime < 3000 ? 'orange' : 'red'}>
                {record.executionTime}ms
              </Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Usuario ID">
              <Text code>{record.userId}</Text>
            </Descriptions.Item>

            <Descriptions.Item label="Admin ID">
              <Text code>{record.adminId}</Text>
            </Descriptions.Item>

            {record.message && (
              <Descriptions.Item label="Mensaje" span={3}>
                <Text type="secondary">{record.message}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Comprobante (si existe) */}
        {record.receipt && (
          <Card
            size="small"
            title={
              <Space>
                <PictureOutlined />
                <span>Comprobante</span>
              </Space>
            }
            extra={
              imageUrl && !imageError && (
                <Button
                  type="link"
                  icon={<ExportOutlined />}
                  onClick={openImageInNewTab}
                  size="small"
                >
                  Abrir en nueva pestaña
                </Button>
              )
            }
          >
            {imageLoading && (
              <Skeleton.Image active style={{ width: '100%', height: 300 }} />
            )}

            {imageError && !imageLoading && (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <Typography.Text type="warning">
                  Error al cargar el comprobante
                </Typography.Text>
                <br />
                <Button
                  type="primary"
                  style={{ marginTop: 16 }}
                  onClick={() => record.receipt && loadReceipt(record.receipt)}
                >
                  Reintentar
                </Button>
                {record.receipt && (record.receipt.startsWith('http://') || record.receipt.startsWith('https://')) && (
                  <>
                    <br />
                    <Button
                      type="link"
                      style={{ marginTop: 8 }}
                      onClick={() => window.open(record.receipt, '_blank')}
                    >
                      Abrir URL externa
                    </Button>
                  </>
                )}
              </div>
            )}

            {imageUrl && !imageLoading && !imageError && (
              <div style={{ textAlign: 'center' }}>
                <Image
                  src={imageUrl}
                  alt="Comprobante"
                  style={{ maxHeight: 400, objectFit: 'contain' }}
                  preview={{
                    mask: 'Click para ampliar'
                  }}
                />
              </div>
            )}
          </Card>
        )}

        {/* Request Data (si existe) */}
        {record.requestData && (
          <Card
            size="small"
            title={
              <Space>
                <FileTextOutlined />
                <span>Request Data (JSON)</span>
              </Space>
            }
            extra={
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(record.requestData, null, 2));
                  message.success('JSON copiado al portapapeles');
                }}
              >
                Copiar
              </Button>
            }
          >
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              <pre style={{
                background: jsonBgColor,
                color: jsonTextColor,
                padding: 12,
                borderRadius: 4,
                fontSize: 12,
                margin: 0,
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {JSON.stringify(record.requestData, null, 2)}
              </pre>
            </div>
          </Card>
        )}

        {/* Placeholder for future features */}
        <Card
          size="small"
          style={{
            background: theme === 'dark' ? '#1a1a1a' : '#f0f0f0',
            border: `1px dashed ${theme === 'dark' ? '#333' : '#d9d9d9'}`
          }}
        >
          <Text type="secondary" style={{ fontStyle: 'italic' }}>
            Próximamente: Historial de cambios y auditoría de operación
          </Text>
        </Card>
      </Space>
    </div>
  );
};
